import React from "react";
import { makeStyles, Tooltip } from "@material-ui/core";
import type { CustomPalette } from "src/theme";

const useStyles = makeStyles((theme) => ({
  pill: {
    display: "flex",
    height: 40,
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: theme.shadows[1],
    border: `1px solid ${theme.palette.divider}`,
  },
  pillSmall: {
    height: 28,
    borderRadius: 14,
  },
  swatch: {
    flex: 1,
    position: "relative",
    transition: "flex 0.2s ease",
    cursor: "pointer",
    "&:hover": {
      flex: 1.5,
    },
  },
  swatchNonInteractive: {
    cursor: "default",
    "&:hover": {
      flex: 1,
    },
  },
}));

export interface ColorPalettePillProps {
  palette: CustomPalette;
  size?: "default" | "small";
  interactive?: boolean;
  showBrandColors?: boolean;
}

interface SwatchConfig {
  key: keyof CustomPalette;
  label: string;
}

const UI_SWATCHES: SwatchConfig[] = [
  { key: "bg", label: "Background" },
  { key: "card", label: "Card" },
  { key: "text", label: "Text" },
  { key: "border", label: "Border" },
  { key: "accent", label: "Accent" },
];

const BRAND_SWATCHES: SwatchConfig[] = [
  { key: "brandPrimary", label: "Brand Primary" },
  { key: "brandSecondary", label: "Brand Secondary" },
];

export function ColorPalettePill({
  palette,
  size = "default",
  interactive = true,
  showBrandColors = true,
}: ColorPalettePillProps) {
  const classes = useStyles();

  const swatches = showBrandColors
    ? [...UI_SWATCHES, ...BRAND_SWATCHES]
    : UI_SWATCHES;

  const pillClassName = `${classes.pill} ${
    size === "small" ? classes.pillSmall : ""
  }`;
  const swatchClassName = `${classes.swatch} ${
    !interactive ? classes.swatchNonInteractive : ""
  }`;

  return (
    <div className={pillClassName}>
      {swatches.map(({ key, label }) => (
        <Tooltip key={key} title={label} placement="top" arrow>
          <div
            className={swatchClassName}
            style={{ backgroundColor: palette[key] }}
          />
        </Tooltip>
      ))}
    </div>
  );
}

export default ColorPalettePill;
